import {
  type EditPartConfigRequest,
  type EditPartConfigService,
  defaultEditPartConfigService,
} from './edit-part-config-service'

/**
 * UID-46 (demo-safety) — the loop-PATCH batch below fires one sequential
 * `PATCH /parts/{partId}` per selected part with no server-side batch
 * endpoint backing it (see this module's own doc comment). An unbounded
 * selection turns "Configure N" / "Apply to multiple parts" into an
 * open-ended run of network round-trips with no way to cancel mid-flight —
 * a real footgun in a demo/no-batch-endpoint world. Callers that open a
 * bulk-configure entry point gate on this cap BEFORE opening the modal
 * (`PartsLibraryPage.handleBulkConfigure`), surfacing an explanatory toast
 * rather than silently truncating the selection.
 */
export const MAX_BULK_CONFIGURE_PARTS = 120

export interface BulkConfigureFailure {
  readonly partId: string
  readonly message: string
}

export interface BulkConfigureOutcome {
  readonly succeeded: readonly string[]
  readonly failed: readonly BulkConfigureFailure[]
}

/**
 * UID-257 — "Configure N" set-based bulk apply for the Parts Library
 * selection bar. The Library has no dedicated bulk-configure endpoint (the
 * only one, `POST /uploads/{sessionId}/staging/bulk-configure`, is staging/
 * session-scoped), so the batch is applied by looping the SAME single-part
 * `PATCH /parts/{partId}` the row-level Edit-configuration modal uses
 * (`edit-part-config-service`). That handler runs a server-side re-DFM +
 * re-price per part (contract: `EditPartConfigRequest`), so re-pricing rides
 * the existing endpoint — the caller just needs to refetch the listing
 * afterward to pick up the new prices.
 *
 * Applied SEQUENTIALLY (mirroring `PartsLibraryPage.handleBulkAddToCart`'s
 * per-row loop) with per-part outcome accounting so a partial failure surfaces
 * exactly which parts didn't take — the succeeded ones are still applied and
 * must not be silently rolled back or double-reported.
 */
export async function bulkConfigureParts(
  partIds: readonly string[],
  request: EditPartConfigRequest,
  service: EditPartConfigService = defaultEditPartConfigService,
): Promise<BulkConfigureOutcome> {
  const succeeded: string[] = []
  const failed: BulkConfigureFailure[] = []

  for (const partId of partIds) {
    try {
      const outcome = await service.editPartConfig(partId, request)
      if (outcome.ok) {
        succeeded.push(partId)
      } else {
        const message =
          outcome.kind === 'field_errors'
            ? (outcome.errors[0]?.detail ?? 'This configuration was rejected.')
            : outcome.message
        failed.push({ partId, message })
      }
    } catch (error) {
      failed.push({
        partId,
        message:
          error instanceof Error ? error.message : 'This configuration could not be applied.',
      })
    }
  }

  return { succeeded, failed }
}
